import { useState, useEffect, useCallback } from 'react';
import {
  Card,
  Col,
  Row,
  Form,
  InputGroup,
  Alert,
  Spinner,
  Modal
} from 'react-bootstrap';
import {
  faHospital,
  faIdCard,
  faFileAlt,
  faEdit,
  faSave,
  faTimes,
  faDownload,
  faUser,
  faMailBulk,
  faKey,
} from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import Button from 'components/base/Button';
import AvatarUpload from 'components/common/AvatarUpload';
import team15 from 'assets/img/team/avatar-rounded.webp';
import {
  getProfile,
  updateProfile,
  downloadDocument,
  viewDocument
} from 'features/profile/profileApi';
import { forgotPassword } from 'features/auth/authApi';
import { useAuth } from 'providers/AuthProvider';
import { getAuthSession } from 'features/auth/authSession';

const normalizeUkPhoneLocal = (value: string) => {
  const digits = value.replace(/\D/g, '');
  if (!digits) return '';
  if (digits.startsWith('44')) {
    return digits.slice(2);
  }
  if (digits.startsWith('0')) {
    return digits.slice(1);
  }
  return digits;
};

const formatUkPhone = (value: string) => {
  const digits = normalizeUkPhoneLocal(value);
  return digits ? `+44${digits}` : '';
};

const PharmacistProfilePage = () => {
  const [isLoading, setIsLoading] = useState(true);
  const [isEditing, setIsEditing] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [profileData, setProfileData] = useState<any>(null);
  const [formData, setFormData] = useState<any>({});
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [selectedDocument, setSelectedDocument] = useState<any>(null);
  const [showDocViewer, setShowDocViewer] = useState(false);
  const [fieldErrors, setFieldErrors] = useState<{ [key: string]: string }>({});
  const [isPasswordResetSending, setIsPasswordResetSending] = useState(false);

  // Required fields for pharmacist profile
  const requiredFields = [
    'name',
    'last_name',
    'pharmacy_name',
    'contact_person',
    'phone_number',
    'address',
    'license_number'
  ];

  const { login } = useAuth();

  // Fetch profile data
  useEffect(() => {
    const fetchProfile = async () => {
      try {
        setIsLoading(true);
        setError(null);
        const data = await getProfile();
        setProfileData(data);
        setFormData({
          name:data.user.name,
          last_name:data.user.last_name,
          ...data.profile
        });
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to load profile');
      } finally {
        setIsLoading(false);
      }
    };

    fetchProfile();
  }, []);

  // Handle form input change
  const handleInputChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value
    });
    // Clear error for this field when user starts typing
    if (fieldErrors[name]) {
      setFieldErrors({
        ...fieldErrors,
        [name]: ''
      });
    }
  };

  // Validate required fields
  const validateForm = (): boolean => {
    const newErrors: { [key: string]: string } = {};

    requiredFields.forEach(field => {
      const value = formData[field];
      if (!value || (typeof value === 'string' && value.trim() === '')) {
        const fieldLabel = field
          .split('_')
          .map(word => word.charAt(0).toUpperCase() + word.slice(1))
          .join(' ');
        newErrors[field] = `${fieldLabel} is required`;
      }
    });

    setFieldErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Handle save
  const handleSave = async () => {
    // Validate form before saving
    if (!validateForm()) {
      setError('Please fill in all required fields');
      return;
    }

    try {
      setIsSaving(true);
      setError(null);
      const dataToSave = { ...formData };
      if (dataToSave.phone_number !== undefined) {
        dataToSave.phone_number = formatUkPhone(dataToSave.phone_number);
      }
      // if (dataToSave.ref_PhoneNumber_1 !== undefined) {
      //   dataToSave.ref_PhoneNumber_1 = formatUkPhone(
      //     dataToSave.ref_PhoneNumber_1
      //   );
      // }  
      // if (dataToSave.ref_PhoneNumber_2 !== undefined) {
      //   dataToSave.ref_PhoneNumber_2 = formatUkPhone(
      //     dataToSave.ref_PhoneNumber_2
      //   );
      // }
      const result = await updateProfile(dataToSave);

      // Get the current session and update it with new user data
      const currentSession = getAuthSession();
      if (currentSession) {
        login({
          token: currentSession.token,
          user: {
            ...currentSession.user,
            name: result.user.name,
            last_name: result.user.last_name
          }
        });
      }

      setSuccess('Profile updated successfully');
      setIsEditing(false);
      setProfileData(result);
      setFormData({
        name: result.user.name,
        last_name: result.user.last_name,
        ...result.profile
      });
      setFieldErrors({});
      setTimeout(() => setSuccess(null), 3000);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update profile');
    } finally {
      setIsSaving(false);
    }
  };

  // Handle cancel
  const handleCancel = () => {
    setFormData(profileData?.profile || {});
    setIsEditing(false);
    setError(null);
    setFieldErrors({});
  };

  // Handle document download
  const handleDocumentDownload = async (document: any) => {
    try {
      console.log('Starting download for:', document.document_name);
      await downloadDocument(document.document_name);
      console.log('Download successful for:', document.document_name);
      setSuccess('Document downloaded successfully');
      setTimeout(() => setSuccess(null), 3000);
    } catch (err) {
      const errorMsg =
        err instanceof Error ? err.message : 'Failed to download document';
      console.error('Download error:', errorMsg);
      setError(errorMsg);
    }
  };

  // Handle document view
  const handleDocumentView = (document: any) => {
    viewDocument(document.document_name);
  };

  const handleChangePassword = async () => {
    const email = profileData?.user?.email;

    if (!email) {
      setError('Unable to find the account email address');
      return;
    }

    try {
      setIsPasswordResetSending(true);
      setError(null);
      setSuccess(null);

      await forgotPassword({ email });

      setSuccess('A password reset link has been sent to your email address.');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to send reset email');
    } finally {
      setIsPasswordResetSending(false);
    }
  };

  if (isLoading) {
    return (
      <div
        className="d-flex justify-content-center align-items-center"
        style={{ minHeight: '400px' }}
      >
        <Spinner animation="border" role="status">
          <span className="visually-hidden">Loading...</span>
        </Spinner>
      </div>
    );
  }

  if (!profileData) {
    return <Alert variant="danger">Failed to load profile</Alert>;
  }

  return (
    <div>
      {error && (
        <Alert variant="danger" onClose={() => setError(null)} dismissible>
          {error}
        </Alert>
      )}
      {success && (
        <Alert variant="success" onClose={() => setSuccess(null)} dismissible>
          {success}
        </Alert>
      )}

      {/* Profile Header Card */}
      <Card className="mb-4">
        <Card.Body>
          <Row className="align-items-center g-4">
            <Col xs={12} sm="auto">
              <AvatarUpload size="5xl" src={team15} />
            </Col>
            <Col xs={12} sm>
              <div className="d-flex justify-content-between align-items-start">
                <div>
                  {isEditing ? (
                    <div className="mb-3">
                      <Row className="g-2">
                        <Col md={6}>
                          <Form.Group className="text-start mb-2">
                            <Form.Label>
                              First Name <span className="text-danger">*</span>
                            </Form.Label>
                            <Form.Control
                              type="text"
                              name="name"
                              value={formData.name || ''}
                              onChange={handleInputChange}
                              placeholder="First Name"
                              isInvalid={!!fieldErrors.name}
                            />
                            {fieldErrors.name && (
                              <Form.Control.Feedback type="invalid" style={{ display: 'block' }}>
                                {fieldErrors.name}
                              </Form.Control.Feedback>
                            )}
                          </Form.Group>
                        </Col>
                        <Col md={6}>
                          <Form.Group className="text-start mb-2">
                            <Form.Label>
                              Last Name <span className="text-danger">*</span>
                            </Form.Label>
                            <Form.Control
                              type="text"
                              name="last_name"
                              value={formData.last_name || ''}
                              onChange={handleInputChange}
                              placeholder="Last Name"
                              isInvalid={!!fieldErrors.last_name}
                            />
                            {fieldErrors.last_name && (
                              <Form.Control.Feedback type="invalid" style={{ display: 'block' }}>
                                {fieldErrors.last_name}
                              </Form.Control.Feedback>
                            )}
                          </Form.Group>
                        </Col>
                      </Row>
                    </div>
                  ) : (
                    <h2 className="mb-1">{formData.name} {formData.last_name}</h2>
                  )}
                  <p className="text-body-secondary mb-2">
                    <FontAwesomeIcon icon={faUser} className="me-2" />
                    Pharmacist
                  </p>
                  <p className="text-body-secondary mb-0">
                    <FontAwesomeIcon icon={faMailBulk} className="me-2" />
                    <a
                      href={`mailto:${profileData.user.email}`}
                      className="text-decoration-none"
                    >
                      {profileData.user.email}
                    </a>
                  </p>
                </div>
                <div>
                  <div className="d-flex flex-column gap-2 align-items-stretch">
                    {!isEditing ? (
                      <Button
                        variant="phoenix-primary"
                        onClick={() => setIsEditing(true)}
                        startIcon={<FontAwesomeIcon icon={faEdit} />}
                        size="sm"
                      >
                        Edit Profile
                      </Button>
                    ) : (
                      <div className="d-flex gap-2">
                        <Button
                          variant="success"
                          onClick={handleSave}
                          loading={isSaving}
                          startIcon={<FontAwesomeIcon icon={faSave} />}
                          size="sm"
                        >
                          Save
                        </Button>
                        <Button
                          variant="secondary"
                          onClick={handleCancel}
                          startIcon={<FontAwesomeIcon icon={faTimes} />}
                          size="sm"
                        >
                          Cancel
                        </Button>
                      </div>
                    )}

                    <Button
                      variant="outline-primary"
                      onClick={handleChangePassword}
                      loading={isPasswordResetSending}
                      startIcon={<FontAwesomeIcon icon={faKey} />}
                      size="sm"
                    >
                      Change Password
                    </Button>
                  </div>
                </div>
              </div>
            </Col>
          </Row>
        </Card.Body>
      </Card>

      {/* Pharmacy Details Card */}
      <Card className="mb-4">
        <Card.Header className="bg-light">
          <Card.Title className="mb-0">
            <FontAwesomeIcon icon={faHospital} className="me-2 text-primary" />
            Pharmacy Information
          </Card.Title>
        </Card.Header>
        <Card.Body>
          <Row className="g-3">
            <Col md={6}>
              <Form.Group className="text-start">
                <Form.Label>
                  Pharmacy Name <span className="text-danger">*</span>
                </Form.Label>
                <Form.Control
                  type="text"
                  name="pharmacy_name"
                  value={formData.pharmacy_name || ''}
                  onChange={handleInputChange}
                  disabled={!isEditing}
                  placeholder="Pharmacy Name"
                  isInvalid={!!fieldErrors.pharmacy_name}
                />
                {fieldErrors.pharmacy_name && (
                  <Form.Control.Feedback type="invalid" style={{ display: 'block' }}>
                    {fieldErrors.pharmacy_name}
                  </Form.Control.Feedback>
                )}
              </Form.Group>
            </Col>
            <Col md={6}>
              <Form.Group className="text-start">
                <Form.Label>
                  Contact Person <span className="text-danger">*</span>
                </Form.Label>
                <Form.Control
                  type="text"
                  name="contact_person"
                  value={formData.contact_person || ''}
                  onChange={handleInputChange}
                  disabled={!isEditing}
                  placeholder="Contact Person"
                  isInvalid={!!fieldErrors.contact_person}
                />
                {fieldErrors.contact_person && (
                  <Form.Control.Feedback type="invalid" style={{ display: 'block' }}>
                    {fieldErrors.contact_person}
                  </Form.Control.Feedback>
                )}
              </Form.Group>
            </Col>
            <Col md={6}>
              <Form.Group className="text-start">
                <Form.Label>
                  Pharmacy Phone <span className="text-danger">*</span>
                </Form.Label>
                <InputGroup>
                  <InputGroup.Text>+44</InputGroup.Text>
                  <Form.Control
                    type="tel"
                    name="phone_number"
                    value={normalizeUkPhoneLocal(formData.phone_number || '')}
                    onChange={
                      isEditing
                        ? e => {
                          const newValue = normalizeUkPhoneLocal(e.target.value);
                          setFormData({
                            ...formData,
                            phone_number: newValue
                          });
                          // Clear error when typing
                          if (fieldErrors.phone_number) {
                            setFieldErrors({
                              ...fieldErrors,
                              phone_number: ''
                            });
                          }
                        }
                        : undefined
                    }
                    disabled={!isEditing}
                    placeholder="7XXXXXXXXX"
                    inputMode="numeric"
                    pattern="\\d{9,10}"
                    title="Enter 9-10 digits after +44"
                    isInvalid={!!fieldErrors.phone_number}
                  />
                </InputGroup>
                {fieldErrors.phone_number && (
                  <Form.Control.Feedback type="invalid" style={{ display: 'block' }}>
                    {fieldErrors.phone_number}
                  </Form.Control.Feedback>
                )}
              </Form.Group>
            </Col>
            <Col md={6}>
              <Form.Group className="text-start">
                <Form.Label>Dispensing System</Form.Label>
                <Form.Control
                  type="text"
                  name="dispensing_system"
                  value={formData.dispensing_system || ''}
                  onChange={handleInputChange}
                  disabled={!isEditing}
                  placeholder="Dispensing System"
                />
              </Form.Group>
            </Col>
            <Col xs={12}>
              <Form.Group className="text-start">
                <Form.Label>
                  Pharmacy Address <span className="text-danger">*</span>
                </Form.Label>
                <Form.Control
                  as="textarea"
                  name="address"
                  value={formData.address || ''}
                  onChange={handleInputChange}
                  disabled={!isEditing}
                  placeholder="Pharmacy Address"
                  style={{ minHeight: '80px' }}
                  isInvalid={!!fieldErrors.address}
                />
                {fieldErrors.address && (
                  <Form.Control.Feedback type="invalid" style={{ display: 'block' }}>
                    {fieldErrors.address}
                  </Form.Control.Feedback>
                )}
              </Form.Group>
            </Col>
            <Col md={6}>
              <Form.Group className="text-start">
                <Form.Label>
                  License Number <span className="text-danger">*</span>
                </Form.Label>
                <Form.Control
                  type="text"
                  name="license_number"
                  value={formData.license_number || ''}
                  onChange={handleInputChange}
                  disabled={!isEditing}
                  placeholder="License Number"
                  isInvalid={!!fieldErrors.license_number}
                />
                {fieldErrors.license_number && (
                  <Form.Control.Feedback type="invalid" style={{ display: 'block' }}>
                    {fieldErrors.license_number}
                  </Form.Control.Feedback>
                )}
              </Form.Group>
            </Col>
          </Row>
        </Card.Body>
      </Card>

      {/* Compliance & Certifications Card */}
      <Card className="mb-5">
        <Card.Header className="bg-light">
          <Card.Title className="mb-0">
            <FontAwesomeIcon icon={faIdCard} className="me-2 text-primary" />
            Compliance Status
          </Card.Title>
        </Card.Header>
        <Card.Body>
          <Row className="g-3">
            <Col md={12}>
              <div className="alert alert-info mb-0" role="alert">
                <strong>License Number:</strong>{' '}
                {profileData.profile.license_number || 'Not provided'}
              </div>
            </Col>
          </Row>
        </Card.Body>
      </Card>

      {/* Professional References Card */}
      {/* <Card className="mb-4">
        <Card.Header className="bg-light">
          <Card.Title className="mb-0">
            <FontAwesomeIcon icon={faIdCard} className="me-2 text-primary" />
            Professional References
          </Card.Title>
        </Card.Header>
        <Card.Body>
          <Row className="g-3">
            <Col md={6}>
              <h6 className="text-body-secondary mb-3">Reference 1</h6>
              <Form.Group className="text-start mb-3">
                <Form.Label>Name</Form.Label>
                <Form.Control
                  type="text"
                  name="ref_Name_1"
                  value={formData.ref_Name_1 || profileData?.user?.ref_Name_1 || ''}
                  onChange={isEditing ? handleInputChange : undefined}
                  disabled={!isEditing}
                  placeholder="Name"
                />
              </Form.Group>
              <Form.Group className="text-start mb-3">
                <Form.Label>Phone</Form.Label>
                <InputGroup>
                  <InputGroup.Text>+44</InputGroup.Text>
                  <Form.Control
                    type="tel"
                    name="ref_PhoneNumber_1"
                    value={normalizeUkPhoneLocal(
                      formData.ref_PhoneNumber_1 ||
                        profileData?.user?.ref_PhoneNumber_1 ||
                        ''
                    )}
                    onChange={
                      isEditing
                        ? e =>
                            setFormData({
                              ...formData,
                              ref_PhoneNumber_1: normalizeUkPhoneLocal(
                                e.target.value
                              )
                            })
                        : undefined
                    }
                    disabled={!isEditing}
                    placeholder="7XXXXXXXXX"
                    inputMode="numeric"
                    pattern="\\d{9,10}"
                    title="Enter 9-10 digits after +44"
                  />
                </InputGroup>
              </Form.Group>
              <Form.Group className="text-start">
                <Form.Label>Details</Form.Label>
                <Form.Control
                  as="textarea"
                  name="ref_Details_1"
                  value={formData.ref_Details_1 || profileData?.user?.ref_Details_1 || ''}
                  onChange={isEditing ? handleInputChange : undefined}
                  disabled={!isEditing}
                  placeholder="Details"
                  style={{ minHeight: '80px' }}
                />
              </Form.Group>
            </Col>
            <Col md={6}>
              <h6 className="text-body-secondary mb-3">Reference 2</h6>
              <Form.Group className="text-start mb-3">
                <Form.Label>Name</Form.Label>
                <Form.Control
                  type="text"
                  name="ref_Name_2"
                  value={formData.ref_Name_2 || profileData?.user?.ref_Name_2 || ''}
                  onChange={isEditing ? handleInputChange : undefined}
                  disabled={!isEditing}
                  placeholder="Name"
                />
              </Form.Group>
              <Form.Group className="text-start mb-3">
                <Form.Label>Phone</Form.Label>
                <InputGroup>
                  <InputGroup.Text>+44</InputGroup.Text>
                  <Form.Control
                    type="tel"
                    name="ref_PhoneNumber_2"
                    value={normalizeUkPhoneLocal(
                      formData.ref_PhoneNumber_2 ||
                        profileData?.user?.ref_PhoneNumber_2 ||
                        ''
                    )}
                    onChange={
                      isEditing
                        ? e =>
                            setFormData({
                              ...formData,
                              ref_PhoneNumber_2: normalizeUkPhoneLocal(
                                e.target.value
                              )
                            })
                        : undefined
                    }
                    disabled={!isEditing}
                    placeholder="7XXXXXXXXX"
                    inputMode="numeric"
                    pattern="\\d{9,10}"
                    title="Enter 9-10 digits after +44"
                  />
                </InputGroup>
              </Form.Group>
              <Form.Group className="text-start">
                <Form.Label>Details</Form.Label>
                <Form.Control
                  as="textarea"
                  name="ref_Details_2"
                  value={formData.ref_Details_2 || profileData?.user?.ref_Details_2 || ''}
                  onChange={isEditing ? handleInputChange : undefined}
                  disabled={!isEditing}
                  placeholder="Details"
                  style={{ minHeight: '80px' }}
                />
              </Form.Group>
            </Col>
          </Row>
        </Card.Body>
      </Card> */}

      {/* Documents Card */}
      {/* <Card className='mb-4'>
        <Card.Header className="bg-light">
          <Card.Title className="mb-0">
            <FontAwesomeIcon icon={faFileAlt} className="me-2 text-primary" />
            Required Documents ({profileData.documents.length})
          </Card.Title>
        </Card.Header>
        <Card.Body>
          {profileData.documents.length > 0 ? (
            <div className="list-group">
              {profileData.documents.map((doc: any) => (
                <div
                  key={doc.id}
                  className="list-group-item d-flex justify-content-between align-items-center"
                >
                  <div className="flex-grow-1">
                    <h6 className="mb-1 d-flex align-items-center">
                      <span
                        className={classNames(
                          'badge me-2',
                          doc.doc_type === 'gphc_number_doc' ||
                            doc.doc_type === 'smartcard'
                            ? 'bg-success'
                            : 'bg-info'
                        )}
                      >
                        {doc.doc_type === 'gphc_number_doc'
                          ? 'GPhC'
                          : doc.doc_type === 'smartcard'
                          ? 'SMARTCARD'
                          : 'CERT'}
                      </span>
                      {doc.doc_type.replace(/_/g, ' ').toUpperCase()}
                    </h6>
                    <small className="text-body-secondary">
                      {doc.document_name}
                    </small>
                  </div>
                  <div className="d-flex gap-2">
                    <Button
                      variant="phoenix-secondary"
                      size="sm"
                      startIcon={<FontAwesomeIcon icon={faEye} />}
                      onClick={() => handleDocumentView(doc)}
                    >
                      View
                    </Button>
                    <Button
                      variant="phoenix-primary"
                      size="sm"
                      startIcon={<FontAwesomeIcon icon={faDownload} />}
                      onClick={() => handleDocumentDownload(doc)}
                    >
                      Download
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <Alert variant="info" className="mb-0">
              No documents uploaded yet
            </Alert>
          )}
        </Card.Body>
      </Card> */}

      {/* Document Viewer Modal */}
      <Modal
        show={showDocViewer}
        onHide={() => setShowDocViewer(false)}
        size="lg"
      >
        <Modal.Header closeButton>
          <Modal.Title>
            {selectedDocument?.doc_type.replace(/_/g, ' ').toUpperCase()}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {selectedDocument && (
            <div className="text-center">
              <p className="mb-3 text-body-secondary">
                {selectedDocument.document_name}
              </p>
              <div
                style={{
                  border: '1px solid #dee2e6',
                  borderRadius: '8px',
                  padding: '20px',
                  backgroundColor: '#f8f9fa',
                  minHeight: '400px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  flexDirection: 'column'
                }}
              >
                <FontAwesomeIcon
                  icon={faFileAlt}
                  size="3x"
                  className="text-muted mb-3"
                />
                <p className="text-muted mb-2">
                  <strong>{selectedDocument.document_name}</strong>
                </p>
                <p className="text-muted small mb-4">
                  Click the button below to view or download this document.
                </p>
                <Button
                  variant="phoenix-primary"
                  startIcon={<FontAwesomeIcon icon={faDownload} />}
                  onClick={() => {
                    handleDocumentDownload(selectedDocument);
                  }}
                >
                  Open Document
                </Button>
              </div>
            </div>
          )}
        </Modal.Body>
      </Modal>
    </div>
  );
};

export default PharmacistProfilePage;
